import React, { useState, useEffect, useMemo } from 'react';
import { Card } from '../../components/ui/Card';
import { 
  TrendingUp, 
  TrendingDown, 
  AlertCircle,
  ArrowUp,
  ArrowDown,
  DollarSign,
  Target,
  ShoppingCart,
  Percent,
  Calendar,
  Globe,
  Mail,
  Bell,
  LayoutGrid,
  Share2,
  Zap,
  ChevronDown,
  ChevronUp,
  Download,
  Layers
} from 'lucide-react';
import { Line } from 'react-chartjs-2';

// ─────────────────────────────────────────────
// UTILITIES
// ─────────────────────────────────────────────

/** Format number as currency */
const fmtCurrency = (v, currency = '₺') => `${currency}${Math.round(v).toLocaleString()}`;

/** Format number with commas */
const fmtNumber = (v) => Math.round(v).toLocaleString();

/** Get month name from Date */
const getMonthLabel = (date) => date.toLocaleString('en-US', { month: 'long', year: 'numeric' });

/** Short month label */
const getShortMonthLabel = (date) => date.toLocaleString('en-US', { month: 'short', year: 'numeric' });

/** Percentage change between two values */
const pctChange = (current, previous) => {
  if (!previous || previous === 0) return 0;
  return ((current - previous) / previous) * 100;
};

// ─────────────────────────────────────────────
// FORECASTING ENGINE
// ─────────────────────────────────────────────

/**
 * Weighted Moving Average - recent months get more weight
 * Returns growth rate multiplier (e.g. 1.06 = +6%)
 */
function calculateWeightedGrowthRate(monthlyValues) {
  if (monthlyValues.length < 2) return 1.03; // fallback
  
  const growthRates = [];
  for (let i = 1; i < monthlyValues.length; i++) {
    if (monthlyValues[i - 1] > 0) {
      growthRates.push(monthlyValues[i] / monthlyValues[i - 1]);
    }
  }
  
  if (growthRates.length === 0) return 1.03;
  
  // Assign weights: most recent gets highest weight
  const weights = growthRates.map((_, i) => i + 1);
  const totalWeight = weights.reduce((s, w) => s + w, 0);
  
  const weightedAvg = growthRates.reduce((sum, rate, i) => sum + rate * weights[i], 0) / totalWeight;
  
  // Clamp between -10% and +20% to avoid wild predictions
  return Math.max(0.90, Math.min(1.20, weightedAvg));
}

/**
 * Generate predictions for N months with decay
 * Further months get pulled toward mean (1.0) for realism
 */
function generatePredictions(lastValue, growthRate, months, decayFactor = 0.15) {
  const predictions = [];
  let current = lastValue;
  let rate = growthRate;
  
  for (let i = 0; i < months; i++) {
    current = Math.round(current * rate);
    predictions.push(current);
    // Decay growth rate toward 1.0 (mean reversion)
    rate = 1 + (rate - 1) * (1 - decayFactor);
  }
  
  return predictions;
}

/**
 * Calculate confidence score
 * Based on data completeness, trend consistency, and prediction distance
 */
function calculateConfidence(monthlyValues, monthAhead) {
  const baseConfidence = 92;
  
  // Penalty for fewer data points
  const dataPointPenalty = Math.max(0, (6 - monthlyValues.length) * 4);
  
  // Penalty for prediction distance
  const distancePenalty = (monthAhead - 1) * 7;
  
  // Penalty for high variance (inconsistent data)
  const growthRates = [];
  for (let i = 1; i < monthlyValues.length; i++) {
    if (monthlyValues[i - 1] > 0) {
      growthRates.push(monthlyValues[i] / monthlyValues[i - 1]);
    }
  }
  
  let variancePenalty = 0;
  if (growthRates.length > 1) {
    const mean = growthRates.reduce((s, v) => s + v, 0) / growthRates.length;
    const variance = growthRates.reduce((s, v) => s + Math.pow(v - mean, 2), 0) / growthRates.length;
    variancePenalty = Math.min(15, Math.round(variance * 500));
  }
  
  return Math.max(40, Math.min(98, baseConfidence - dataPointPenalty - distancePenalty - variancePenalty));
}

/**
 * Scenario multipliers
 */
const SCENARIOS = {
  best: { label: 'Best Case', multiplier: 1.15, color: '#22c55e', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  expected: { label: 'Expected', multiplier: 1.0, color: '#3b82f6', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  worst: { label: 'Worst Case', multiplier: 0.85, color: '#ef4444', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' }
};

// ─────────────────────────────────────────────
// CHART CONFIG
// ─────────────────────────────────────────────

const baseChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: {
      position: 'top',
      labels: {
        usePointStyle: true,
        pointStyle: 'circle',
        padding: 20,
        font: { size: 12, family: "'Inter', sans-serif" }
      }
    },
    tooltip: {
      backgroundColor: '#fff',
      titleColor: '#1f2937',
      bodyColor: '#1f2937',
      borderColor: '#e5e7eb',
      borderWidth: 1,
      padding: 12,
      bodyFont: { size: 12, family: "'Inter', sans-serif" },
      titleFont: { size: 12, family: "'Inter', sans-serif", weight: '600' },
      callbacks: {
        label: (ctx) => {
          const label = ctx.dataset.label || '';
          const val = ctx.parsed.y;
          if (val === null || val === undefined) return null;
          return `${label}: ₺${Math.round(val).toLocaleString()}`;
        }
      }
    }
  },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 12 }, color: '#6b7280' } },
    y: {
      grid: { color: '#f3f4f6' },
      ticks: {
        font: { size: 12 },
        color: '#6b7280',
        callback: (v) => `₺${(v / 1000).toFixed(0)}K`
      }
    }
  }
};

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────

export default function RevenueForcasting() {
  const [predictionMonths, setPredictionMonths] = useState(3);
  const [activeScenario, setActiveScenario] = useState('expected');
  const [expandedChannels, setExpandedChannels] = useState({});
  
  // Data states
  const [unifiedCampaignData, setUnifiedCampaignData] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(true);

  // ── Fetch Data ──
  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingData(true);
      try {
        const brandId = localStorage.getItem('currentBrandId');
        if (!brandId) return;
        
        const DEMO_IDS = new Set([
          '2b08baa8-dd8e-4b5c-93cd-dd38b9b3718a',
          'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4'
        ]);
        
        if (DEMO_IDS.has(brandId)) {
          // Demo data
          const channels = ['Google Ads', 'Meta Ads', 'Google Organic', 'Direct', 'Email', 'Referral'];
          const today = new Date();
          const demo = [];
          const prng = (seed) => { let x = Math.sin(seed) * 10000; return x - Math.floor(x); };
          const vary = (base, pct, seed) => Math.max(0, Math.round(base * (1 + ((prng(seed) - 0.5) * 2) * pct)));
          
          for (let i = 6; i >= 1; i--) {
            const m = new Date(today.getFullYear(), today.getMonth() - i, 1);
            channels.forEach((ch, ci) => {
              const seed = i * 17 + ci * 31 + 7;
              const sessionsBase = [42000, 30000, 52000, 17000, 12000, 15000][ci];
              const crBase = [0.021, 0.023, 0.018, 0.015, 0.020, 0.016][ci];
              const aovBase = [165, 158, 145, 172, 155, 148][ci];
              const sessions = vary(sessionsBase, 0.18, seed + 1);
              const transactions = Math.max(1, Math.round(sessions * crBase * (1 + (prng(seed + 2) - 0.5) * 0.15)));
              const revenue = Math.max(1000, Math.round(transactions * aovBase * (1 + (prng(seed + 3) - 0.5) * 0.12)));
              demo.push({
                channel: ch,
                date: new Date(m.getFullYear(), m.getMonth(), 15).toISOString().slice(0, 10),
                ga4_total_revenue: revenue,
                ga4_sessions: sessions,
                ga4_purchases: transactions,
                google_cost: ch.includes('Google') ? Math.round(revenue * 0.28) : 0,
                meta_cost: ch.includes('Meta') ? Math.round(revenue * 0.32) : 0,
              });
            });
          }
          setUnifiedCampaignData(demo);
          return;
        }
        
        const response = await fetch(
          'https://api.glowytics.com/api/google-ads/unified-campaign-performance?dateRange=all',
          { headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId }, credentials: 'include' }
        );
        
        if (response.ok) {
          setUnifiedCampaignData(await response.json());
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoadingData(false);
      }
    };
    fetchData();
  }, []);

  // ── Process Historical Data ──
  const processedData = useMemo(() => {
    if (!unifiedCampaignData || unifiedCampaignData.length === 0) return null;
    
    const currentDate = new Date();
    const currentMonthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
    
    // Group by month and channel (exclude current month)
    const monthlyData = {};
    
    unifiedCampaignData.forEach(item => {
      const date = new Date(item.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (monthKey === currentMonthKey) return; // Skip current partial month
      
      const channel = item.channel || 'Unknown';
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          monthName: getMonthLabel(date),
          date: new Date(date.getFullYear(), date.getMonth(), 1),
          channels: {}
        };
      }
      
      if (!monthlyData[monthKey].channels[channel]) {
        monthlyData[monthKey].channels[channel] = { revenue: 0, sessions: 0, transactions: 0, cost: 0 };
      }
      
      const ch = monthlyData[monthKey].channels[channel];
      ch.revenue += Number(item.ga4_total_revenue || 0);
      ch.sessions += Number(item.ga4_sessions || 0);
      ch.transactions += Number(item.ga4_purchases || 0);
      
      if (channel.includes('Google')) ch.cost += Number(item.google_cost || 0);
      if (channel.includes('Meta')) ch.cost += Number(item.meta_cost || 0);
    });
    
    // Sort and take last 6 months
    const sortedMonths = Object.keys(monthlyData).sort().slice(-6);
    
    const historicalData = sortedMonths.map(monthKey => {
      const month = monthlyData[monthKey];
      
      // Calculate derived metrics
      Object.values(month.channels).forEach(ch => {
        ch.cr = ch.sessions > 0 ? (ch.transactions / ch.sessions) * 100 : 0;
        ch.aov = ch.transactions > 0 ? ch.revenue / ch.transactions : 0;
      });
      
      // Totals
      const totals = Object.values(month.channels).reduce((acc, ch) => ({
        revenue: acc.revenue + ch.revenue,
        sessions: acc.sessions + ch.sessions,
        transactions: acc.transactions + ch.transactions,
        cost: acc.cost + ch.cost
      }), { revenue: 0, sessions: 0, transactions: 0, cost: 0 });
      
      totals.cr = totals.sessions > 0 ? (totals.transactions / totals.sessions) * 100 : 0;
      totals.aov = totals.transactions > 0 ? totals.revenue / totals.transactions : 0;
      
      return { monthKey, monthName: month.monthName, date: month.date, channels: month.channels, totals };
    });
    
    const channels = [...new Set(unifiedCampaignData.map(item => item.channel || 'Unknown'))];
    
    return { channels, historicalData };
  }, [unifiedCampaignData]);

  // ── Predictions ──
  const predictions = useMemo(() => {
    if (!processedData || processedData.historicalData.length < 2) return null;
    
    const hist = processedData.historicalData;
    const lastMonth = hist[hist.length - 1];
    
    // Overall predictions
    const revenueValues = hist.map(m => m.totals.revenue);
    const transactionValues = hist.map(m => m.totals.transactions);
    const sessionValues = hist.map(m => m.totals.sessions);
    
    const revenueGrowth = calculateWeightedGrowthRate(revenueValues);
    const transactionGrowth = calculateWeightedGrowthRate(transactionValues);
    const sessionGrowth = calculateWeightedGrowthRate(sessionValues);
    
    const predictedRevenues = generatePredictions(lastMonth.totals.revenue, revenueGrowth, predictionMonths);
    const predictedTransactions = generatePredictions(lastMonth.totals.transactions, transactionGrowth, predictionMonths);
    const predictedSessions = generatePredictions(lastMonth.totals.sessions, sessionGrowth, predictionMonths);
    
    // Build month labels
    const futureMonths = [];
    for (let i = 1; i <= predictionMonths; i++) {
      const d = new Date(lastMonth.date);
      d.setMonth(d.getMonth() + i);
      futureMonths.push({ date: d, label: getMonthLabel(d), shortLabel: getShortMonthLabel(d) });
    }
    
    // Channel-specific predictions
    const channelPredictions = {};
    processedData.channels.forEach(channel => {
      const chRevenueValues = hist.map(m => (m.channels[channel]?.revenue || 0));
      const chSessionValues = hist.map(m => (m.channels[channel]?.sessions || 0));
      const chTransValues = hist.map(m => (m.channels[channel]?.transactions || 0));
      
      const chRevenueGrowth = calculateWeightedGrowthRate(chRevenueValues);
      const chSessionGrowth = calculateWeightedGrowthRate(chSessionValues);
      const chTransGrowth = calculateWeightedGrowthRate(chTransValues);
      
      const lastCh = lastMonth.channels[channel] || { revenue: 0, sessions: 0, transactions: 0 };
      
      channelPredictions[channel] = {
        growthRate: chRevenueGrowth,
        months: futureMonths.map((fm, i) => {
          const rev = generatePredictions(lastCh.revenue, chRevenueGrowth, i + 1).pop() || 0;
          const sess = generatePredictions(lastCh.sessions, chSessionGrowth, i + 1).pop() || 0;
          const trans = generatePredictions(lastCh.transactions, chTransGrowth, i + 1).pop() || 0;
          const cr = sess > 0 ? (trans / sess) * 100 : 0;
          const aov = trans > 0 ? rev / trans : 0;
          return { ...fm, revenue: rev, sessions: sess, transactions: trans, cr, aov, confidence: calculateConfidence(chRevenueValues, i + 1) };
        })
      };
    });
    
    // Summary for cards
    const summary = futureMonths.map((fm, i) => ({
      ...fm,
      revenue: predictedRevenues[i],
      transactions: predictedTransactions[i],
      sessions: predictedSessions[i],
      aov: predictedTransactions[i] > 0 ? predictedRevenues[i] / predictedTransactions[i] : 0,
      revenueChange: pctChange(predictedRevenues[i], i === 0 ? lastMonth.totals.revenue : predictedRevenues[i - 1]),
      transactionChange: pctChange(predictedTransactions[i], i === 0 ? lastMonth.totals.transactions : predictedTransactions[i - 1]),
      aovChange: pctChange(
        predictedTransactions[i] > 0 ? predictedRevenues[i] / predictedTransactions[i] : 0,
        i === 0 ? lastMonth.totals.aov : (predictedTransactions[i - 1] > 0 ? predictedRevenues[i - 1] / predictedTransactions[i - 1] : 0)
      ),
      revenueConfidence: calculateConfidence(revenueValues, i + 1),
      transactionConfidence: calculateConfidence(transactionValues, i + 1),
      aovConfidence: calculateConfidence(revenueValues, i + 1) - 5
    }));
    
    return { summary, futureMonths, channelPredictions, revenueGrowth, transactionGrowth };
  }, [processedData, predictionMonths]);

  // ── Chart Data ──
  const chartData = useMemo(() => {
    if (!processedData || !predictions) return { labels: [], datasets: [] };
    
    const hist = processedData.historicalData;
    const histLabels = hist.map(m => getShortMonthLabel(m.date));
    const histValues = hist.map(m => m.totals.revenue);
    const predLabels = predictions.futureMonths.map(m => m.shortLabel);
    
    const scenarioMultiplier = SCENARIOS[activeScenario].multiplier;
    const predValues = predictions.summary.map(m => Math.round(m.revenue * scenarioMultiplier));
    
    // Confidence band (worst to best)
    const upperBand = predictions.summary.map(m => Math.round(m.revenue * SCENARIOS.best.multiplier));
    const lowerBand = predictions.summary.map(m => Math.round(m.revenue * SCENARIOS.worst.multiplier));
    
    return {
      labels: [...histLabels, ...predLabels],
      datasets: [
        {
          label: 'Historical',
          data: [...histValues, ...Array(predLabels.length).fill(null)],
          borderColor: '#1d1d1f',
          backgroundColor: 'rgba(29, 29, 31, 0.05)',
          fill: false,
          tension: 0.3,
          borderWidth: 2.5,
          pointRadius: 4,
          pointBackgroundColor: '#1d1d1f',
          pointBorderColor: '#fff',
          pointBorderWidth: 2
        },
        {
          label: `Forecast (${SCENARIOS[activeScenario].label})`,
          data: [...Array(histValues.length - 1).fill(null), histValues[histValues.length - 1], ...predValues],
          borderColor: SCENARIOS[activeScenario].color,
          backgroundColor: 'transparent',
          fill: false,
          tension: 0.3,
          borderWidth: 2.5,
          borderDash: [6, 4],
          pointRadius: 5,
          pointBackgroundColor: SCENARIOS[activeScenario].color,
          pointBorderColor: '#fff',
          pointBorderWidth: 2
        },
        {
          label: 'Confidence Band (Upper)',
          data: [...Array(histValues.length - 1).fill(null), histValues[histValues.length - 1], ...upperBand],
          borderColor: 'rgba(34, 197, 94, 0.3)',
          backgroundColor: 'rgba(34, 197, 94, 0.05)',
          fill: '+1',
          tension: 0.3,
          borderWidth: 1,
          borderDash: [3, 3],
          pointRadius: 0
        },
        {
          label: 'Confidence Band (Lower)',
          data: [...Array(histValues.length - 1).fill(null), histValues[histValues.length - 1], ...lowerBand],
          borderColor: 'rgba(239, 68, 68, 0.3)',
          backgroundColor: 'rgba(239, 68, 68, 0.05)',
          fill: false,
          tension: 0.3,
          borderWidth: 1,
          borderDash: [3, 3],
          pointRadius: 0
        }
      ]
    };
  }, [processedData, predictions, activeScenario]);

  // Toggle channel expand
  const toggleChannel = (ch) => setExpandedChannels(prev => ({ ...prev, [ch]: !prev[ch] }));

  // ── Loading ──
  if (isLoadingData) {
    return (
      <div className="p-6 flex justify-center items-center h-[500px]">
          <div className="text-center">
            <div className="loading-logo-container mb-4">
            <img src="/logoicon.png" alt="Loading" className="loading-logo mx-auto" />
            </div>
          <p className="text-gray-500">Loading revenue forecasting data...</p>
          </div>
      </div>
    );
  }

  if (!processedData || processedData.historicalData.length < 2) {
    return (
      <div className="p-6 flex justify-center items-center h-[400px]">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-800 mb-2">Not Enough Data</h2>
          <p className="text-gray-500">Revenue forecasting requires at least 2 months of historical data. Connect your ad accounts and wait for data to accumulate.</p>
        </div>
      </div>
    );
  }

  const nextMonthLabel = predictions?.futureMonths[0]?.label || 'Next Month';
  const scenarioMultiplier = SCENARIOS[activeScenario].multiplier;

  return (
    <div className="p-6 space-y-6">
      
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl shadow-sm">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Revenue Forecasting</h1>
          <p className="text-sm text-gray-500 mt-1">
            Trend-based predictions from {processedData.historicalData.length} months of data across {processedData.channels.length} channels
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Scenario Selector */}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {Object.entries(SCENARIOS).map(([key, s]) => (
              <button
                key={key}
                onClick={() => setActiveScenario(key)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  activeScenario === key
                    ? `${s.bg} ${s.text}`
                    : 'bg-white text-gray-500 hover:bg-gray-50'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          {/* Month Selector */}
          <select
            value={predictionMonths}
            onChange={(e) => setPredictionMonths(Number(e.target.value))}
            className="px-3 py-1.5 border border-gray-200 rounded-lg bg-white text-sm text-gray-600"
          >
            <option value={1}>1 Month</option>
            <option value={3}>3 Months</option>
            <option value={6}>6 Months</option>
          </select>
        </div>
      </div>

      {/* ── Prediction Summary Cards ── */}
      {predictions && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <PredictionCard 
              title="Predicted Revenue"
            value={Math.round(predictions.summary[0].revenue * scenarioMultiplier)}
            change={predictions.summary[0].revenueChange}
            confidence={predictions.summary[0].revenueConfidence}
              icon={DollarSign}
              format="currency"
            period={predictions.futureMonths[0].label}
            growthRate={predictions.revenueGrowth}
            />
            <PredictionCard 
            title="Predicted Orders"
            value={Math.round(predictions.summary[0].transactions * scenarioMultiplier)}
            change={predictions.summary[0].transactionChange}
            confidence={predictions.summary[0].transactionConfidence}
              icon={ShoppingCart}
              format="number"
            period={predictions.futureMonths[0].label}
            growthRate={predictions.transactionGrowth}
            />
            <PredictionCard 
              title="Avg. Order Value"
            value={Math.round(predictions.summary[0].aov * (scenarioMultiplier > 1 ? 1 + (scenarioMultiplier - 1) * 0.3 : 1 - (1 - scenarioMultiplier) * 0.3))}
            change={predictions.summary[0].aovChange}
            confidence={predictions.summary[0].aovConfidence}
              icon={Target}
              format="currency"
            period={predictions.futureMonths[0].label}
          />
          {/* Confidence Overview */}
          <Card className={`p-5 rounded-2xl ${SCENARIOS[activeScenario].bg} border ${SCENARIOS[activeScenario].border}`}>
            <div className="flex items-start gap-3">
              <div className={`p-2.5 rounded-xl bg-white/80`}>
                <Layers className={`w-5 h-5 ${SCENARIOS[activeScenario].text}`} />
                </div>
                <div>
                <h3 className="text-sm font-medium text-gray-600">Forecast Model</h3>
                <p className={`text-2xl font-bold mt-1 ${SCENARIOS[activeScenario].text}`}>
                  {Math.round((predictions.summary[0].revenueConfidence + predictions.summary[0].transactionConfidence) / 2)}%
                </p>
                <p className="text-xs text-gray-500 mt-1">Avg. confidence ({SCENARIOS[activeScenario].label})</p>
                <p className="text-xs text-gray-400 mt-0.5">Weighted Moving Average</p>
                </div>
              </div>
            </Card>
          </div>
        )}

      {/* ── Revenue Chart ── */}
      <Card className="p-6 rounded-2xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Revenue Trend & Forecast</h2>
            <p className="text-sm text-gray-500">
              {processedData.historicalData.length} historical months + {predictionMonths} predicted
              <span className="ml-2 text-amber-600">· Current month excluded</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 rounded-md">
              <div className="w-3 h-0.5 bg-gray-800 rounded"></div>
              <span className="text-xs text-gray-500">Historical</span>
            </div>
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 rounded-md">
              <div className="w-3 h-0.5 rounded" style={{ borderBottom: `2px dashed ${SCENARIOS[activeScenario].color}` }}></div>
              <span className="text-xs text-gray-500">Forecast</span>
            </div>
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 rounded-md">
              <div className="w-3 h-3 rounded bg-gradient-to-b from-green-100 to-red-100 opacity-50"></div>
              <span className="text-xs text-gray-500">Range</span>
          </div>
        </div>
                </div>
        <div className="h-[380px]">
          <Line data={chartData} options={baseChartOptions} />
        </div>
      </Card>

      {/* ── Scenario Comparison Table ── */}
      {predictions && (
      <Card className="rounded-2xl overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-800">Scenario Comparison</h2>
            <p className="text-sm text-gray-500 mt-1">Best, expected, and worst case revenue projections</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left py-3 px-5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Month</th>
                  {Object.entries(SCENARIOS).map(([key, s]) => (
                    <th key={key} className={`text-right py-3 px-5 text-xs font-semibold uppercase tracking-wider ${s.text}`}>{s.label}</th>
                  ))}
                  <th className="text-right py-3 px-5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Confidence</th>
              </tr>
            </thead>
              <tbody className="divide-y divide-gray-50">
                {/* Historical rows */}
                {processedData.historicalData.map((m, i) => {
                  const prev = i > 0 ? processedData.historicalData[i - 1] : null;
                  const change = prev ? pctChange(m.totals.revenue, prev.totals.revenue) : null;
                return (
                    <tr key={m.monthKey} className="hover:bg-gray-50/50">
                      <td className="py-3 px-5 text-sm font-medium text-gray-700">{m.monthName}</td>
                      <td className="py-3 px-5 text-sm text-right text-gray-600" colSpan={3}>
                        {fmtCurrency(m.totals.revenue)}
                        {change !== null && (
                          <span className={`ml-2 text-xs ${change >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                            {change >= 0 ? '+' : ''}{change.toFixed(1)}%
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-5 text-sm text-right text-gray-400">—</td>
                  </tr>
                );
                })}
                {/* Prediction rows */}
                {predictions.summary.map((m, i) => (
                  <tr key={m.label} className="bg-blue-50/30 hover:bg-blue-50/50">
                    <td className="py-3 px-5 text-sm font-medium text-blue-700">
                      {m.label}
                      <span className="ml-1 text-xs text-blue-400">predicted</span>
                      </td>
                    <td className="py-3 px-5 text-sm text-right font-medium text-green-700">
                      {fmtCurrency(m.revenue * SCENARIOS.best.multiplier)}
                      </td>
                    <td className="py-3 px-5 text-sm text-right font-medium text-blue-700">
                      {fmtCurrency(m.revenue)}
                      </td>
                    <td className="py-3 px-5 text-sm text-right font-medium text-red-600">
                      {fmtCurrency(m.revenue * SCENARIOS.worst.multiplier)}
                      </td>
                    <td className="py-3 px-5 text-sm text-right">
                      <ConfidenceBadge value={m.revenueConfidence} />
                      </td>
                    </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>
      )}

      {/* ── Channel Breakdown ── */}
      {predictions && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Channel Forecasts</h2>
              <p className="text-sm text-gray-500">Per-channel growth rates and predictions</p>
      </div>
          </div>
          
          {processedData.channels.map(channel => {
            const chPred = predictions.channelPredictions[channel];
            if (!chPred) return null;
            
            const isExpanded = expandedChannels[channel];
            const hist = processedData.historicalData;
            const lastCh = hist[hist.length - 1]?.channels[channel];
            if (!lastCh || lastCh.revenue === 0) return null;
            
            const growthPct = ((chPred.growthRate - 1) * 100).toFixed(1);
            const nextPred = chPred.months[0];
          
          return (
              <Card key={channel} className="rounded-2xl overflow-hidden">
                {/* Channel Header */}
                <button
                  onClick={() => toggleChannel(channel)}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <ChannelIcon channel={channel} />
                    <div className="text-left">
                      <span className="font-medium text-gray-800">{channel}</span>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-xs text-gray-500">
                          Last: {fmtCurrency(lastCh.revenue)}
                        </span>
                        <span className="text-xs text-gray-400">→</span>
                        <span className={`text-xs font-medium ${Number(growthPct) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                          Next: {fmtCurrency(Math.round(nextPred.revenue * scenarioMultiplier))}
                          <span className="ml-1">({Number(growthPct) >= 0 ? '+' : ''}{growthPct}%/mo)</span>
                        </span>
                  </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <ConfidenceBadge value={nextPred.confidence} />
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </div>
                </button>

                {/* Expanded Table */}
                {isExpanded && (
                  <div className="overflow-x-auto border-t border-gray-100">
                <table className="w-full">
                  <thead>
                        <tr className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          <th className="py-2.5 px-4 text-left">Month</th>
                          <th className="py-2.5 px-4 text-right">Sessions</th>
                          <th className="py-2.5 px-4 text-right">CR</th>
                          <th className="py-2.5 px-4 text-right">Orders</th>
                          <th className="py-2.5 px-4 text-right">AOV</th>
                          <th className="py-2.5 px-4 text-right">Revenue</th>
                    </tr>
                  </thead>
                      <tbody className="text-sm divide-y divide-gray-50">
                        {hist.map(m => {
                          const ch = m.channels[channel] || { revenue: 0, sessions: 0, transactions: 0, cr: 0, aov: 0 };
                      return (
                            <tr key={m.monthKey} className="hover:bg-gray-50/50">
                              <td className="py-2.5 px-4 font-medium text-gray-600">{m.monthName}</td>
                              <td className="py-2.5 px-4 text-right text-gray-600">{fmtNumber(ch.sessions)}</td>
                              <td className="py-2.5 px-4 text-right text-gray-600">{ch.cr.toFixed(2)}%</td>
                              <td className="py-2.5 px-4 text-right text-gray-600">{fmtNumber(ch.transactions)}</td>
                              <td className="py-2.5 px-4 text-right text-gray-600">{fmtCurrency(ch.aov)}</td>
                              <td className="py-2.5 px-4 text-right text-gray-600">{fmtCurrency(ch.revenue)}</td>
                        </tr>
                      );
                    })}
                        {chPred.months.map((pred, i) => (
                          <tr key={pred.label} className="bg-blue-50/40 hover:bg-blue-50/60">
                            <td className="py-2.5 px-4 font-medium text-blue-700">
                              {pred.label} <span className="text-xs text-blue-400">predicted</span>
                            </td>
                            <td className="py-2.5 px-4 text-right text-blue-700">{fmtNumber(Math.round(pred.sessions * scenarioMultiplier))}</td>
                            <td className="py-2.5 px-4 text-right text-blue-700">{pred.cr.toFixed(2)}%</td>
                            <td className="py-2.5 px-4 text-right text-blue-700">{fmtNumber(Math.round(pred.transactions * scenarioMultiplier))}</td>
                            <td className="py-2.5 px-4 text-right text-blue-700">{fmtCurrency(pred.aov)}</td>
                            <td className="py-2.5 px-4 text-right text-blue-700">{fmtCurrency(Math.round(pred.revenue * scenarioMultiplier))}</td>
                          </tr>
                        ))}
                  </tbody>
                </table>
              </div>
                )}
            </Card>
          );
        })}
        </div>
      )}

      {/* ── Methodology Note ── */}
      <div className="text-center py-4">
        <p className="text-xs text-gray-400">
          Predictions use a Weighted Moving Average model with mean reversion decay. Confidence scores factor in data completeness, trend consistency, and prediction horizon. Past performance does not guarantee future results.
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// SUBCOMPONENTS
// ─────────────────────────────────────────────

function PredictionCard({ title, value, change, confidence, icon: Icon, format, period, growthRate }) {
  const formatted = format === 'currency' ? fmtCurrency(value) : fmtNumber(value);
  const isPositive = change >= 0;

  return (
    <Card className="p-5 rounded-2xl">
      <div className="flex items-center justify-between mb-3">
        <div className="p-2.5 rounded-xl bg-gray-100">
          <Icon className="w-4 h-4 text-gray-600" />
        </div>
        <span className="text-xs text-gray-400">{period}</span>
        </div>
      <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">{title}</h3>
      <p className="text-2xl font-bold text-gray-800 mt-1">{formatted}</p>
      <div className="flex items-center gap-2 mt-2">
        <div className={`flex items-center gap-0.5 ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
          {isPositive ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />}
          <span className="text-sm font-medium">{Math.abs(change).toFixed(1)}%</span>
      </div>
        <span className="text-xs text-gray-400">vs prev.</span>
      </div>
      <div className="mt-2">
        <ConfidenceBar value={confidence} />
      </div>
    </Card>
  );
} 

function ConfidenceBar({ value }) {
  const color = value >= 80 ? 'bg-green-500' : value >= 60 ? 'bg-amber-500' : 'bg-red-400';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full bg-gray-100">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${value}%` }} />
      </div>
      <span className="text-xs text-gray-400 w-8 text-right">{value}%</span>
    </div>
  );
}

function ConfidenceBadge({ value }) {
  const color = value >= 80 ? 'bg-green-100 text-green-700' : value >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-600';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {value}%
    </span>
  );
}

function ChannelIcon({ channel }) {
  if (channel.includes('Google') && channel.includes('Organic')) return <Globe className="w-5 h-5 text-green-600" />;
  if (channel.includes('Google')) return <img src="/google_ads-512.webp" className="w-5 h-5" alt="Google" />;
  if (channel.includes('Meta')) return <img src="/6033716.png" className="w-5 h-5" alt="Meta" />;
  if (channel === 'Direct') return <Globe className="w-5 h-5 text-gray-500" />;
  if (channel === 'Email') return <Mail className="w-5 h-5 text-gray-500" />;
  if (channel === 'Push') return <Bell className="w-5 h-5 text-gray-500" />;
  if (channel.includes('Referral')) return <Share2 className="w-5 h-5 text-gray-500" />;
  if (channel.includes('Criteo') || channel.includes('RTB')) return <Zap className="w-5 h-5 text-gray-500" />;
  return <LayoutGrid className="w-5 h-5 text-gray-500" />;
}
